/**
 * @file Publishes `CompositeFieldContext` for one composite field: its identity,
 * its value, the two write paths a sub-field takes, and the map of sub-fields the
 * platform computes. Consumers: `TableModeContent` and `FormModeContent`, which
 * mount it around the sidebar's `LayoutUI`, and every `useFieldUI` below it.
 * The computed map is the only thing that locks a sub-field: a dotted projection
 * column and a composite formula target both write without a `_calculated`
 * marker, because the sub-fields beside the one they own stay the person's.
 */
import { FC, ReactNode, useCallback, useContext, useMemo } from 'react';
import { Timestamp } from 'firebase/firestore';
import { AgentType, CompositeFieldValue } from '#types/System.types';
import type { ArCellValue, ArFormulaEntry, CompoundSubFieldChangePayload } from '#types';
import { formDeriveCopy } from '@/services/formDerives';
import { UserContext } from '../../providers/UserContext';
import { CompositeFieldContext } from './CompositeFieldContext';

const emptyComputedSubFieldReasons: ReadonlyMap<string, string> = new Map();

type CompositeFieldModeProps = {
  compositeFieldId: string;
  compositeFieldTypeId: string;
  entityId?: never;
};

type EntityModeProps = {
  entityId: string;
  compositeFieldId?: never;
  compositeFieldTypeId?: never;
};

type CompositeFieldProviderProps = {
  value: CompositeFieldValue | undefined;
  isFormulaColumn?: boolean;
  formulaSequence?: ArFormulaEntry[];
  /**
   * Sub-fields a Form Derive projection fills, each mapped to the sentence the
   * projected cell shows on hover. The second source of the context's disabled
   * map: the platform writes them, so a person may read them and not edit them,
   * exactly as with a formula target. The sentence arrives already composed
   * rather than being written again here — the cell and the sidebar have to word
   * one derive one way.
   */
  derivedSubFieldReasons?: ReadonlyMap<string, string>;
  onChange?: (value: ArCellValue) => void;
  tableMode?: boolean;
  children: ReactNode;
} & (CompositeFieldModeProps | EntityModeProps);

export const CompositeFieldProvider: FC<CompositeFieldProviderProps> = (props) => {
  const {
    value,
    isFormulaColumn = false,
    formulaSequence,
    derivedSubFieldReasons,
    onChange,
    tableMode = false,
    children,
  } = props;

  const compositeFieldId = 'entityId' in props && props.entityId ? props.entityId : props.compositeFieldId!;
  const compositeFieldTypeId = 'entityId' in props && props.entityId ? props.entityId : props.compositeFieldTypeId!;
  const { user } = useContext(UserContext);

  /**
   * Both sources in one map, keyed by sub-field id. A formula target is worded
   * here, from the deck, because this is the only place that knows a sub-field is
   * one; a projected sub-field brings its own sentence in. A key both sources
   * claim keeps the formula sentence, which is the truer one — the reconciler
   * skips a target the table's formulas already own.
   */
  const computedSubFieldReasons = useMemo(() => {
    if (!formulaSequence?.length && !derivedSubFieldReasons?.size) return emptyComputedSubFieldReasons;
    const reasons = new Map<string, string>(derivedSubFieldReasons);
    for (const e of formulaSequence ?? []) {
      const tid = e.typeId as string;
      const dotIdx = tid.indexOf('.');
      if (dotIdx >= 0) {
        if (tid.slice(0, dotIdx) === compositeFieldId) {
          reasons.set(tid.slice(dotIdx + 1), formDeriveCopy.subFieldFormulaOwned);
        }
      } else {
        reasons.set(tid, formDeriveCopy.subFieldFormulaOwned);
      }
    }
    return reasons;
  }, [formulaSequence, derivedSubFieldReasons, compositeFieldId]);

  const setSubFieldValue = useCallback((fieldId: string, fieldValue: unknown) => {
    if (!onChange) return;

    const fieldEntry = {
      value: fieldValue,
      meta: {
        lastModified: Timestamp.now(),
        userId: user?.uid,
        agentType: AgentType.User,
      },
    };

    if (tableMode) {
      onChange({
        kind: 'subField',
        subField: { container: 'fields', key: fieldId },
        value: fieldEntry,
      } satisfies CompoundSubFieldChangePayload);
      return;
    }

    onChange({
      ...value,
      fields: {
        ...value?.fields,
        [fieldId]: fieldEntry,
      },
    } as ArCellValue);
  }, [onChange, user?.uid, value, tableMode]);

  const setEntityReference = useCallback((referenceKey: string, data: { entityRecordId: string | undefined; entityRecordName: string }) => {
    if (!onChange) return;

    const refEntry = {
      entityRecordId: data.entityRecordId,
      entityRecordName: data.entityRecordName,
      meta: {
        lastModified: Timestamp.now(),
        userId: user?.uid,
        agentType: AgentType.User,
      },
    };

    if (tableMode) {
      onChange({
        kind: 'subField',
        subField: { container: 'entityReferences', key: referenceKey },
        value: refEntry,
      } satisfies CompoundSubFieldChangePayload);
      return;
    }

    onChange({
      ...value,
      entityReferences: {
        ...value?.entityReferences,
        [referenceKey]: refEntry,
      },
    } as ArCellValue);
  }, [onChange, user?.uid, value, tableMode]);

  const contextValue = useMemo(() => ({
    compositeFieldId,
    compositeFieldTypeId,
    compositeFieldValue: value,
    isFormulaColumn,
    computedSubFieldReasons,
    setSubFieldValue,
    setEntityReference,
  }), [compositeFieldId, compositeFieldTypeId, value, isFormulaColumn, computedSubFieldReasons, setSubFieldValue, setEntityReference]);

  return (
    <CompositeFieldContext.Provider value={contextValue}>
      {children}
    </CompositeFieldContext.Provider>
  );
};
